import moment from "moment";
import TourCalculationService from "./TourCalculationService";

/**
 * Servicio para validar y corregir cálculos de precios de tours
 * Basado en el análisis de las imágenes proporcionadas
 */
class TourPricingValidator {
  /**
   * Valida los días efectivos del tour
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Datos de precios actuales
   * @returns {Object} Resultado de la validación
   */
  static validateEffectiveDays(tourData, pricing) {
    if (!tourData || !tourData.booking || !tourData.booking.tour) {
      return {
        isValid: false,
        error: "Datos del tour no disponibles"
      };
    }

    const expectedDays = TourCalculationService.calculateEffectiveDays(
      tourData.booking.tour.effective_start_date,
      tourData.booking.tour.effective_end_date
    );

    const actualDays = (pricing && pricing.breakdown && pricing.breakdown.effectiveDays) || 0;

    return {
      isValid: expectedDays === actualDays,
      expectedDays,
      actualDays,
      difference: expectedDays - actualDays,
      error: expectedDays !== actualDays ? 
        `Días efectivos incorrectos: esperado ${expectedDays}, calculado ${actualDays}` : null
    };
  }

  /**
   * Valida el cálculo del GPS
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Datos de precios actuales
   * @returns {Object} Resultado de la validación del GPS
   */
  static validateGpsCalculation(tourData, pricing) {
    const daysValidation = this.validateEffectiveDays(tourData, pricing);
    
    if (!daysValidation.isValid) {
      return {
        isValid: false,
        error: daysValidation.error,
        correction: null
      };
    }

    const gpsPrice = (pricing && pricing.breakdown && pricing.breakdown.gpsPrice) || 0;
    const effectiveDays = daysValidation.expectedDays;
    const dailyRate = 10.00; // €10 por día según las imágenes

    const expectedGpsPrice = dailyRate * effectiveDays;
    const actualGpsPrice = gpsPrice;

    const isValid = Math.abs(expectedGpsPrice - actualGpsPrice) < 0.01;

    return {
      isValid,
      expectedGpsPrice,
      actualGpsPrice,
      dailyRate,
      effectiveDays,
      correction: !isValid ? {
        from: actualGpsPrice,
        to: expectedGpsPrice,
        dailyRate,
        days: effectiveDays
      } : null,
      error: !isValid ? 
        `GPS mal calculado: esperado €${expectedGpsPrice.toFixed(2)}, actual €${actualGpsPrice.toFixed(2)}` : null
    };
  }

  /**
   * Valida el cálculo del seguro VIP
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Datos de precios actuales
   * @returns {Object} Resultado de la validación del seguro VIP
   */
  static validateVipInsuranceCalculation(tourData, pricing) {
    const daysValidation = this.validateEffectiveDays(tourData, pricing);
    
    if (!daysValidation.isValid) {
      return {
        isValid: false,
        error: daysValidation.error,
        correction: null
      };
    }

    const vipInsurancePrice = (pricing && pricing.breakdown && pricing.breakdown.vipInsurancePrice) || 0;
    const effectiveDays = daysValidation.expectedDays;
    
    // Calcular tarifa diaria basada en el precio actual
    const dailyRate = vipInsurancePrice / ((pricing && pricing.breakdown && pricing.breakdown.effectiveDays) || 1);
    const expectedVipInsurancePrice = dailyRate * effectiveDays;
    const actualVipInsurancePrice = vipInsurancePrice;

    const isValid = Math.abs(expectedVipInsurancePrice - actualVipInsurancePrice) < 0.01;

    return {
      isValid,
      expectedVipInsurancePrice,
      actualVipInsurancePrice,
      dailyRate,
      effectiveDays,
      correction: !isValid ? {
        from: actualVipInsurancePrice,
        to: expectedVipInsurancePrice,
        dailyRate,
        days: effectiveDays
      } : null,
      error: !isValid ? 
        `Seguro VIP mal calculado: esperado €${expectedVipInsurancePrice.toFixed(2)}, actual €${actualVipInsurancePrice.toFixed(2)}` : null
    };
  }

  /**
   * Valida el cálculo del total del tour
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Datos de precios actuales
   * @returns {Object} Resultado de la validación del total
   */
  static validateTotalCalculation(tourData, pricing) {
    const gpsValidation = this.validateGpsCalculation(tourData, pricing);
    const vipValidation = this.validateVipInsuranceCalculation(tourData, pricing);
    
    const breakdown = (pricing && pricing.breakdown) || {};
    const basePrice = breakdown.basePrice || 7565.00;
    const passengerPrice = breakdown.passengerPrice || 3935.00;
    
    // Usar precios corregidos si están disponibles
    const gpsPrice = gpsValidation.correction ? gpsValidation.correction.to : (breakdown.gpsPrice || 0);
    const vipInsurancePrice = vipValidation.correction ? vipValidation.correction.to : (breakdown.vipInsurancePrice || 0);
    
    const expectedTotal = basePrice + passengerPrice + gpsPrice + vipInsurancePrice;
    const actualTotal = (pricing && pricing.finalTotal) || (pricing && pricing.total) || 0;

    const isValid = Math.abs(expectedTotal - actualTotal) < 0.01;

    return {
      isValid,
      expectedTotal,
      actualTotal,
      breakdown: {
        basePrice,
        passengerPrice,
        gpsPrice,
        vipInsurancePrice
      },
      error: !isValid ? 
        `Total mal calculado: esperado €${expectedTotal.toFixed(2)}, actual €${actualTotal.toFixed(2)}` : null
    };
  }

  /**
   * Valida todos los cálculos de precios
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Datos de precios actuales
   * @returns {Object} Resultado completo de la validación
   */
  static validateAllCalculations(tourData, pricing) {
    const daysValidation = this.validateEffectiveDays(tourData, pricing);
    const gpsValidation = this.validateGpsCalculation(tourData, pricing);
    const vipValidation = this.validateVipInsuranceCalculation(tourData, pricing);
    const totalValidation = this.validateTotalCalculation(tourData, pricing);

    const allValid = daysValidation.isValid && 
                    gpsValidation.isValid && 
                    vipValidation.isValid && 
                    totalValidation.isValid;

    const errors = [];
    const warnings = [];
    const corrections = {};

    if (!daysValidation.isValid) {
      errors.push(daysValidation.error);
    }

    if (!gpsValidation.isValid) {
      errors.push(gpsValidation.error);
      if (gpsValidation.correction) {
        corrections.gps = gpsValidation.correction;
      }
    }

    if (!vipValidation.isValid) {
      errors.push(vipValidation.error);
      if (vipValidation.correction) {
        corrections.vipInsurance = vipValidation.correction;
      }
    }

    if (!totalValidation.isValid) {
      errors.push(totalValidation.error);
    }

    // Advertencias adicionales
    if (daysValidation.expectedDays !== daysValidation.actualDays) {
      warnings.push(`Los días efectivos deberían ser ${daysValidation.expectedDays} en lugar de ${daysValidation.actualDays}`);
    }

    return {
      isValid: allValid,
      errors,
      warnings,
      corrections,
      validations: {
        days: daysValidation,
        gps: gpsValidation,
        vipInsurance: vipValidation,
        total: totalValidation
      }
    };
  }

  /**
   * Aplica correcciones a los cálculos de precios
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Datos de precios actuales
   * @returns {Object} Precios corregidos
   */
  static applyCorrections(tourData, pricing) {
    const validation = this.validateAllCalculations(tourData, pricing);
    
    if (validation.isValid) {
      return pricing; // No hay correcciones necesarias
    }

    const correctedPricing = { ...pricing };
    const correctedBreakdown = { ...(pricing && pricing.breakdown) };

    // Aplicar corrección de días efectivos
    if (validation.validations.days.expectedDays !== validation.validations.days.actualDays) {
      correctedBreakdown.effectiveDays = validation.validations.days.expectedDays;
    }

    // Aplicar corrección de GPS
    if (validation.corrections.gps) {
      correctedBreakdown.gpsPrice = validation.corrections.gps.to;
    }

    // Aplicar corrección de seguro VIP
    if (validation.corrections.vipInsurance) {
      correctedBreakdown.vipInsurancePrice = validation.corrections.vipInsurance.to;
    }

    // Recalcular total
    const basePrice = correctedBreakdown.basePrice || 7565.00;
    const passengerPrice = correctedBreakdown.passengerPrice || 3935.00;
    const gpsPrice = correctedBreakdown.gpsPrice || 0;
    const vipInsurancePrice = correctedBreakdown.vipInsurancePrice || 0;

    const correctedTotal = basePrice + passengerPrice + gpsPrice + vipInsurancePrice;

    correctedPricing.breakdown = correctedBreakdown;
    correctedPricing.finalTotal = correctedTotal;
    correctedPricing.total = correctedTotal;

    return {
      original: pricing,
      corrected: correctedPricing,
      validation
    };
  }

  /**
   * Genera un reporte de validación en formato legible
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Datos de precios actuales
   * @returns {Object} Reporte de validación
   */
  static generateValidationReport(tourData, pricing) {
    const validation = this.validateAllCalculations(tourData, pricing);
    const corrections = this.applyCorrections(tourData, pricing);

    return {
      summary: {
        isValid: validation.isValid,
        totalErrors: validation.errors.length,
        totalWarnings: validation.warnings.length,
        hasCorrections: Object.keys(validation.corrections).length > 0
      },
      details: {
        days: {
          expected: validation.validations.days.expectedDays,
          actual: validation.validations.days.actualDays,
          isValid: validation.validations.days.isValid
        },
        gps: {
          expected: validation.validations.gps.expectedGpsPrice,
          actual: validation.validations.gps.actualGpsPrice,
          dailyRate: validation.validations.gps.dailyRate,
          isValid: validation.validations.gps.isValid
        },
        total: {
          expected: validation.validations.total.expectedTotal,
          actual: validation.validations.total.actualTotal,
          isValid: validation.validations.total.isValid
        }
      },
      corrections: validation.corrections,
      errors: validation.errors,
      warnings: validation.warnings,
      correctedPricing: corrections.corrected
    };
  }
}

export default TourPricingValidator; 


// WEBPACK FOOTER //
// ./src/services/TourPricingValidator.js